import {
  AppConstants,
  conceptCodes,
  EMAIL_VALIDATION_ERROR,
  FORM_VALUES_KEYS,
  MLY_IDS,
  NAME_CATEGORY_BASED_QA_PREFIX,
  RegexConstants,
  SESSION_CORRELATIONID,
  SESSION_EMAIL_VALIDATION_ERROR,
} from "../../../constants/constant";
import {
  Field,
  FormValues,
  SchedulerConfig,
} from "../../../Interface/scheduler/form";
import {
  fetchFranchiseLookupByServiceAddress,
  fetchFranchisePosLookup,
  fetchISNRedirectConfigURLs,
  fetchSchedulingByFullAttribute,
} from "../../../service/apiService";
import BookingPayloadModel from "../../../service/model/BookingPayloadInterface";
import isInboundFromNbly from "../../../utility/checkInBoundFromNbly";
import { sessionStorageUtils, getRandomInt } from "../../../utility/utility";

export const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const fullMonths = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const weekdayAbbreviations = {
  Sunday: "Sun",
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
};

const weekdaysFull = {
  Sun: "Sunday",
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
};

const formatPhoneNumber = (value: string) => {
  if (!value) return value;

  const phoneNumber = value.replace(/[^\d]/g, "");
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

/**
 * Formats a phone input value and maintains the correct cursor position safely.
 *
 * @param input - The HTML input element.
 * @param rawValue - The unformatted raw value of the input.
 * @param formatter - A function that returns the formatted value.
 * @returns The formatted string.
 */
function formatPhoneInputWithCursorPosition(
  input: HTMLInputElement,
  rawValue: string,
  formatter: (val: string) => string,
): string {
  try {
    // Defensive fallback: if input is not in DOM or has no selection
    const cursorPos =
      typeof input.selectionStart === "number"
        ? input.selectionStart
        : rawValue.length;

    // Count how many digits are before the current cursor position
    const digitsBeforeCursor = rawValue
      .slice(0, cursorPos)
      .replace(/\D/g, "").length;

    const formattedValue = formatter?.(rawValue) ?? "";

    // Use setTimeout to wait until value is applied before setting cursor
    setTimeout(() => {
      if (!input || typeof input.setSelectionRange !== "function") return;

      let digitCount = 0;

      const newCursorIndex = [...formattedValue].findIndex((char) => {
        if (/\d/.test(char)) digitCount++;
        return digitCount === digitsBeforeCursor;
      });

      const finalCursor =
        newCursorIndex === -1 ? formattedValue.length : newCursorIndex + 1;

      try {
        input.setSelectionRange(finalCursor, finalCursor);
      } catch (err) {
        console.warn("Unable to set selection range:", err);
      }
    }, 0);

    return formattedValue;
  } catch (error) {
    console.error("formatPhoneInputWithCursorPosition error:", error);
    return rawValue; // fallback to unformatted value on failure
  }
}

const validatePhoneNumber = (phoneNumber: string) => {
  const numRegex = RegexConstants.PHONE_REG;
  phoneNumber = phoneNumber?.replace(/\D/g, "");
  const x = phoneNumber.match(numRegex);
  const formattedVal = x ? `(${x[1]}) ${x[2]}${x[3] ? "-" + x[3] : ""}` : "";
  if (phoneNumber.length < 14 && formattedVal.length < 14) {
    return false;
  }
  return true;
};

const validateEmail = (email: string) => {
  const emailRegex = RegexConstants.EMAIL_REG;
  const trimmedEmail = email.trim();
  return emailRegex.test(trimmedEmail);
};

export const isInvalidZipcode = (addressLabel: string | undefined): boolean => {
  const invalidZipcodeRegex = /\b\d{5}-\d{4}\b/;
  return addressLabel ? invalidZipcodeRegex.test(addressLabel) : false;
};

const validateZipCode = (zipCode: string) => {
  const trimmedZipCode = zipCode.trim();
  const domain = checkDomainCode();

  // Check if the zip code is "00000"
  if (trimmedZipCode === "00000") {
    return false;
  }

  if (domain === "ca") {
    const zipCodeRegex = RegexConstants.US_CA_ZIP_CODE_REG;
    return zipCodeRegex.test(trimmedZipCode);
  } else {
    const zipCodeRegex = RegexConstants.ZIP_CODE_REG;
    return zipCodeRegex.test(trimmedZipCode);
  }
};

const validateFirstNameAndLast = (firstName: string) => {
  const firstNameRegex = RegexConstants.FIRST_LAST_NAME_REG;
  return firstNameRegex.test(firstName);
};

const validateAddress = (address: string) => {
  const addressRegex = RegexConstants.ADDRESS_REG;
  if (address.startsWith(" ")) {
    return false;
  }
  return addressRegex.test(address);
};

const decodeHtmlEntities = (encodedString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(encodedString, "text/html");
  return doc.documentElement.textContent;
};

const generateRandomString = (length: number) => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(getRandomInt(charactersLength));
  }
  return result;
};

const getSessionAndCorrelationID = () => {
  const obj = {
    correlationID: "",
    sessionID: "",
  };

  const sessionID = localStorage.getItem("sessionIDFr");
  const correlationID = sessionStorage.getItem(SESSION_CORRELATIONID);
  if (sessionID) {
    obj.sessionID = sessionID;
  } else {
    obj.sessionID = generateRandomString(5);
    localStorage.setItem("sessionIDFr", obj.sessionID);
  }

  if (correlationID) {
    obj.correlationID = correlationID;
  } else {
    obj.correlationID = `${generateRandomString(5)}-${Date.now()}`;
  }
  return obj;
};

const formatSelectedDate = (dateString) => {
  const [datePart, weekdayShort] = dateString.split("/");
  const [year, month, day] = datePart.split("-");
  const formattedDay = day?.padStart(2, "0");
  const monthName = fullMonths[parseInt(month) - 1];
  const weekdayFull = weekdaysFull[weekdayShort];
  return `${weekdayFull}, ${monthName} ${formattedDay}, ${year}`;
};

const isWeekendDate = (dateString) => {
  const [, weekdayShort] = dateString.split("/");
  const weekdayFull = weekdaysFull[weekdayShort];
  return weekdayFull === "Saturday" || weekdayFull === "Sunday";
};

function formatMobileHeaderDate(dateKey: string) {
  if (!dateKey) return "";
  const [datePart, weekdayShort] = dateKey.split("/");
  const [, month, day] = datePart.split("-");
  const formattedDay = day?.padStart(2, "0");
  const monthName = fullMonths[parseInt(month) - 1];
  const weekdayFull = weekdaysFull[weekdayShort];
  return `${weekdayFull}, ${monthName} ${formattedDay}`;
}

const formatFormValueDate = (dateString) => {
  if (dateString) {
    const [weekdayFull, monthDay, year] = dateString.split(", ");
    const [month, day] = monthDay.split(" ");
    const monthIndex = fullMonths.indexOf(month);
    const formattedMonth = (monthIndex + 1).toString().padStart(2, "0");
    const weekdayShort = weekdayAbbreviations[weekdayFull];
    return `${year}-${formattedMonth}-${day.padStart(2, "0")}/${weekdayShort}/${monthIndex}`;
  } else {
    return "";
  }
};

export function storeBookingApiData(payload: BookingPayloadModel, response) {
  try {
    const formdata = JSON.stringify(payload);
    sessionStorage.setItem("rl-post-data", formdata);
    const apiData =
      '{ "Result": ' +
      JSON.stringify(response) +
      ', "Form data": ' +
      formdata +
      ', "Response message":"SUCCESS" }';
    sessionStorage.setItem("NEI-API data", apiData);
  } catch (error) {
    console.log("Error while storing the apipayload", error);
  }
}

const schedulerConfigJsonSequence = (data: SchedulerConfig) => {
  try {
    if (data?.steps) {
      const stepOrder = data.steps?.sort(
        (a, b) => Number(a.sequence) - Number(b.sequence),
      );
      const visibleSteps = stepOrder.filter((step) => !step.hideStep);

      // Remove steps with null or undefined sequences
      const stepsWithValidSequence = visibleSteps.filter(
        (step) => step.sequence != null && step.sequence !== "",
      );

      // Remove duplicate sequences, keeping the first occurrence
      const seenSequences = new Set<string>();
      const uniqueSteps = stepsWithValidSequence.filter((step) => {
        if (seenSequences.has(step.sequence)) {
          return false;
        }
        seenSequences.add(step.sequence);
        return true;
      });

      return { ...data, steps: uniqueSteps };
    }
  } catch (error) {
    console.log("Error while sorting the steps", error);
  }
};
const updateFormValuesWithCategoryBasedQAs = (
  prevValues: FormValues,
  name: string,
  value: string,
  question?: string,
) => {
  if (name.startsWith(NAME_CATEGORY_BASED_QA_PREFIX)) {
    const questionUISequence = Number(name.split("_")[1]);
    const newQuestion = { question, answer: value, questionUISequence };
    const categoryBasedQAs =
      prevValues[FORM_VALUES_KEYS.CATEGORY_BASED_QAS] || [];
    const existingIndex = categoryBasedQAs.findIndex(
      (qa) => qa.questionUISequence === questionUISequence,
    );

    if (existingIndex > -1) {
      // Update the existing question
      categoryBasedQAs[existingIndex] = {
        ...categoryBasedQAs[existingIndex],
        ...newQuestion,
      };
    } else {
      // Append the new question
      categoryBasedQAs.push(newQuestion);
    }

    return {
      ...prevValues,
      categoryBasedQAs,
    };
  }
};

function createGuid(): string {
  function _p8(s?: boolean): string {
    const p = (getRandomInt(1000).toString(16) + "000000000").substr(2, 8);
    return s ? `-${p.substr(0, 4)}-${p.substr(4, 4)}` : p;
  }
  return _p8(false) + _p8(true) + _p8(true) + _p8(false);
}

function getFileExtension(fileName: string): string {
  return fileName.split(".").pop()?.toLowerCase() || "";
}

function getFranchiseIdByLocationId(
  allNearByLocationsWithExtraData,
  franchiseWebLocationId,
) {
  for (const franchise of allNearByLocationsWithExtraData) {
    for (const detail of franchise.franchiseDetails) {
      if (detail.franchiseWebLocationId === franchiseWebLocationId) {
        return franchise.franchiseId;
      }
    }
  }
  return null;
}
function checkDomainCode(): "ca" | "us" {
  if (isInboundFromNbly()) {
    const pathname = new URL(globalThis.location.href).pathname;
    return pathname.startsWith("/ca") ? "ca" : "us";
  }

  const origin = globalThis.location.origin;
  return origin.includes("-ca") || origin.includes(".ca") ? "ca" : "us";
}

const isCaPostalCode = (zipcode: string) => {
  return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i.test(
    zipcode,
  );
};
const isUsPostalCode = (zipcode: string) => {
  return /^([0-9]{5})$/.test(zipcode?.trim());
};

const getEmailValidationError = async (field: Field, email: string) => {
  const abortController = new AbortController();
  const signal = abortController.signal;
  const conceptCode = sessionStorageUtils.getItem("conceptCode");
  const historyCount = 5;

  const objProp = {
    value: email,
    conceptCode,
    signal,
    historyCount,
  };
  try {
    const errMsg = await Promise.resolve(window?.ValidateEmail(objProp));
    if (errMsg === "EmptyField") {
      sessionStorageUtils.removeItem(SESSION_EMAIL_VALIDATION_ERROR);
      return `${field.errorMessage}`;
    } else if (errMsg === "RegexFailed") {
      sessionStorageUtils.removeItem(SESSION_EMAIL_VALIDATION_ERROR);
      return `${field.label} ${EMAIL_VALIDATION_ERROR}`;
    } else if (
      errMsg === "SendGridResponseFalse" ||
      errMsg === "SendGridFailed"
    ) {
      sessionStorageUtils.setItem(SESSION_EMAIL_VALIDATION_ERROR, true);
      return `SendGridResponseFalse`;
    } else {
      return null;
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      console.warn("Email validation aborted");
    } else {
      console.error("Error during email validation:", error);
    }
  }
};

const getValidFormattedDate = (
  formValues: { date?: string },
  dates: string[],
  firstAvailableDate?: string,
): string => {
  const formValueDate = formValues?.date;
  if (
    !formValueDate ||
    formValueDate === "Invalid Date" ||
    formValueDate?.includes("undefined")
  ) {
    return firstAvailableDate || (dates.length > 0 ? dates[0] : "");
  } else if (
    typeof formValueDate === "string" &&
    formValueDate.split(", ").length === 3
  ) {
    return formatFormValueDate(formValueDate);
  } else {
    return firstAvailableDate || (dates.length > 0 ? dates[0] : "");
  }
};

const validateSquareFeet = (value: string): string => {
  const numericValue = value.replace(/\D/g, "");
  const truncatedValue = numericValue.slice(0, 5);
  let squareFeet = "";
  if (truncatedValue !== "") {
    const parsedValue = parseInt(truncatedValue, 10);
    squareFeet = parsedValue === 0 ? "" : parsedValue.toString();
  }
  return squareFeet;
};

const extractDomain = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return urlObj.origin;
  } catch (error) {
    console.error("Invalid URL:", error);
    return "";
  }
};

const updateResidentialDetails = (
  payload: BookingPayloadModel,
  formValues: FormValues,
) => {
  if (formValues?.["cleaning-service"] === MLY_IDS.RESIDENTIAL) {
    if (formValues?.["frequency-of-cleaning"] === MLY_IDS.ONE_TIME_CLEAN) {
      payload.estimateSquareFeet = formValues?.["square-feet"];
      payload.estimateBedrooms = formValues?.bedrooms;
      payload.estimateBathrooms = formValues?.bathrooms;
      const isMoveInOut = formValues?.["one-time-clean-type"];
      if (isMoveInOut === MLY_IDS.MOVE_IN_OUT_CLEANING_OPTION) {
        payload.jobFrequencyDetail = MLY_IDS.MOVE_IN_OUT_CLEAN;
      } else {
        payload.jobFrequencyDetail = MLY_IDS.NOT_MOVE_IN_OUT_CLEAN;
      }
    } else {
      payload.estimateSquareFeet = "";
      payload.estimateBedrooms = "";
      payload.estimateBathrooms = "";
      payload.jobFrequency = formValues["frequency-of-cleaning"];
      payload.jobFrequencyDetail = "";
    }
  }
};

const updateCommercialDetails = (
  payload: BookingPayloadModel,
  formValues: FormValues,
) => {
  if (formValues?.["cleaning-service"] === MLY_IDS.COMMERCIAL) {
    if (
      formValues?.["commercial-frequency"] === MLY_IDS.COMMERCIAL_ONE_TIME_CLEAN
    ) {
      const isCommercialMoveInOut = formValues?.["commercial-move-clean"];
      if (isCommercialMoveInOut === "yes") {
        payload.jobFrequencyDetail = MLY_IDS.MOVE_IN_OUT_CLEAN;
      } else {
        payload.jobFrequencyDetail = MLY_IDS.NOT_MOVE_IN_OUT_CLEAN;
      }
    } else {
      payload.jobFrequencyDetail = formValues?.["recurring-schedule"];
    }
    payload.estimateSquareFeet = formValues?.["office-square-feet"];
    payload.estimateBedrooms = formValues?.["offices"];
    payload.estimateBathrooms = formValues?.["commercial-bathrooms"];
    payload.jobFrequency = formValues?.["commercial-frequency"];
  }
};

const handleOfferDetails = (
  formConfig,
  formValues: FormValues,
  setFormValues,
  setOfferRemoved,
) => {
  const offerDetails = JSON.parse(sessionStorageUtils.getItem("offerDetails"));
  if (
    formConfig?.conceptCode === conceptCodes.MDG_CONCEPT_CODE &&
    offerDetails?.servicemodifier?.length > 0
  ) {
    const serviceType = formValues?.servicetype;
    const serviceModifier = offerDetails.servicemodifier || [];
    const updateServiceText = serviceModifier.includes(serviceType);
    if (!updateServiceText) {
      sessionStorageUtils.removeItem("offerDetails");
      setFormValues((prevValues) => ({
        ...prevValues,
        offerRemoved: true,
        isServiceModifier: true,
      }));
      setOfferRemoved(true);
    } else {
      setFormValues((prevValues) => ({
        ...prevValues,
        offerRemoved: false,
        isServiceModifier: false,
      }));
      setOfferRemoved(false);
    }
  }
};

const handleISNRedirect = async (
  posLookupResArr,
  onlineSchedulingAvailable,
  setCurrentStep,
  locationWebsiteUrl,
  conceptCode,
  setIsLoading,
) => {
  setIsLoading?.(true);
  try {
    const posLookupRes = Array.isArray(posLookupResArr)
      ? posLookupResArr[0]
      : posLookupResArr;

    if (posLookupRes?.posName === AppConstants.ISN) {
      if (onlineSchedulingAvailable) {
        try {
          const isnConfigList = await fetchISNRedirectConfigURLs(conceptCode);
          const isnObj = isnConfigList?.find(
            ({ franchisee_number }) =>
              franchisee_number &&
              posLookupRes?.franchiseWebLocationMarketingId &&
              franchisee_number ===
                posLookupRes.franchiseWebLocationMarketingId,
          );
          if (isnObj?.isnUrl && isnObj.isnUrl !== "\\N") {
            const popup = window.open(isnObj.isnUrl, "_blank");
            if (locationWebsiteUrl) {
              if (popup) {
                setTimeout(() => {
                  globalThis.location.href = locationWebsiteUrl;
                }, 150);
              } else {
                alert(
                  "Please allow pop-ups to continue. HouseMaster is a Neighborly company, and you’ll be redirected to Neighborly.com to complete your scheduled request.",
                );
              }
            }
            return;
          }
        } catch (err) {
          console.error("Error fetching ISN config:", err);
          return;
        }
        setCurrentStep((prev) => prev + 1);
        return;
      } else {
        setCurrentStep((prev) => prev + 1);
        return;
      }
    }

    setCurrentStep((prev) => prev + 1);
  } finally {
    setIsLoading?.(false);
  }
};

const handleStoredWebLocationRedirect = async (
  currentStep,
  setFormValues,
  setCurrentStep,
  formConfig,
  storedWebLocationId,
  setIsLoading,
) => {
  if (storedWebLocationId && currentStep === 0) {
    if (typeof window.GetCachedResponse === "function") {
      setIsLoading?.(true);
      setFormValues((prev) => ({
        ...prev,
        franchiseWebLocationId: storedWebLocationId,
      }));
      try {
        const onlineSchedulingRes =
          await fetchSchedulingByFullAttribute(storedWebLocationId);
        const posLookupResArr =
          await fetchFranchisePosLookup(storedWebLocationId);
        const franchiseDetails = await fetchFranchiseLookupByServiceAddress(
          onlineSchedulingRes.zip,
          formConfig?.conceptid,
          "",
        );
        const locationWebsiteUrl = franchiseDetails?.[0]?.locationWebsiteUrl;
        setFormValues((prev) => ({
          ...prev,
          zipcode: onlineSchedulingRes.zip,
        }));
        await handleISNRedirect(
          posLookupResArr,
          onlineSchedulingRes.onlineScheduling,
          setCurrentStep,
          locationWebsiteUrl,
          formConfig.conceptCode,
          setIsLoading,
        );
      } catch (error) {
        console.error(
          "Error fetching POS Lookup for stored WebLocationID:",
          error,
        );
      }
      setIsLoading?.(false);
    }
  }
};
const handleHMSMultipleLocationPOS = async (
  franchiseWebLocationId,
  setCurrentStep,
  setIsLoading,
  conceptCode,
) => {
  try {
    const posLookupRes = await fetchFranchisePosLookup(
      franchiseWebLocationId.toString(),
    );
    const onlineSchedulingRes = await fetchSchedulingByFullAttribute(
      franchiseWebLocationId.toString(),
    );

    await handleISNRedirect(
      posLookupRes,
      onlineSchedulingRes?.onlineScheduling,
      setCurrentStep,
      onlineSchedulingRes?.locationWebsiteUrl,
      conceptCode,
      setIsLoading,
    );
    setIsLoading(false);
  } catch (error) {
    console.error("Error fetching POS for HMS single location:", error);
    setIsLoading(false);
  }
};
const handleStoredZipcode = async (
  storedZipcode,
  formConfig,
  setCurrentStep,
  setIsLoading,
  setFormValues,
) => {
  if (typeof window.GetCachedResponse === "function") {
    setIsLoading?.(true);
    try {
      const res = await fetchFranchiseLookupByServiceAddress(
        storedZipcode,
        formConfig?.conceptid,
        "",
      );
      if (res && res.length > 0) {
        const franchiseWebLocationId = res[0]?.franchiseWebLocationId;
        const franchiseId = res[0]?.franchiseId;
        setFormValues((prev) => ({
          ...prev,
          franchiseId: franchiseId,
          franchiseWebLocationId: franchiseWebLocationId,
        }));
        await handleHMSMultipleLocationPOS(
          franchiseWebLocationId,
          setCurrentStep,
          setIsLoading,
          formConfig.conceptCode,
        );
      }
    } catch (error) {
      console.error("Error processing stored zipcode:", error);
    } finally {
      setIsLoading?.(false);
    }
  }
};

const handleMraStoredZipcode = async (
  storedZipcode,
  formConfig,
  setCurrentStep,
  setIsLoading,
  setOnline,
  setFormValues,
) => {
  if (typeof window.GetCachedResponse === "function") {
    setIsLoading?.(true);
    try {
      const res = await fetchFranchiseLookupByServiceAddress(
        storedZipcode,
        formConfig?.conceptid,
        "",
      );
      if (res && res.length > 0) {
        const franchiseWebLocationId = res[0]?.franchiseWebLocationId;
        const franchiseId = res[0]?.franchiseId;
        setFormValues((prev) => ({
          ...prev,
          franchiseId: franchiseId,
          franchiseWebLocationId: franchiseWebLocationId,
        }));
        await fetchSchedulingByFullAttribute(franchiseWebLocationId);
        setCurrentStep((prevStep) => prevStep + 1);
        setOnline(true);
      }
    } catch (error) {
      console.error("Error processing MRA stored zipcode:", error);
    } finally {
      setIsLoading?.(false);
    }
  }
};
const handleStoredWebLocationRedirectMRA = async (
  currentStep,
  setFormValues,
  setCurrentStep,
  formConfig,
  storedWebLocationId,
  setIsLoading,
  setOnline,
) => {
  if (storedWebLocationId && currentStep === 0) {
    if (typeof window.GetCachedResponse === "function") {
      setIsLoading?.(true);
      try {
        const schedulingRes =
          await fetchSchedulingByFullAttribute(storedWebLocationId);
        const franchiseDetails = await fetchFranchiseLookupByServiceAddress(
          schedulingRes.zip,
          formConfig?.conceptid,
          "",
        );

        if (franchiseDetails && franchiseDetails.length > 0) {
          const franchiseId = franchiseDetails[0]?.franchiseId;
          setFormValues((prev) => ({
            ...prev,
            franchiseWebLocationId: storedWebLocationId,
            franchiseId: franchiseId,
            zipcode: schedulingRes.zip,
          }));
        }
        setCurrentStep((prev) => prev + 1);
        setOnline(true);
      } catch (error) {
        console.error("Error in handleStoredWebLocationRedirectMRA:", error);
      } finally {
        setIsLoading?.(false);
      }
    }
  }
};

const skipToNextStep = ({
  storedZipcode,
  setFormValues,
  currentStepConfig,
  isNeighborlyFlow,
  currentStep,
  formConfig,
  setCurrentStep,
  setIsLoading,
  setOnline,
  storedWebLocationId,
}) => {
  if (storedZipcode) {
    setFormValues((prevValues) => ({
      ...prevValues,
      zipcode: storedZipcode,
    }));

    if (
      currentStepConfig?.steptype === AppConstants.BOOK_AN_INSPECTION &&
      isNeighborlyFlow &&
      currentStep === 0
    ) {
      const code = formConfig?.conceptCode;
      if (code === conceptCodes.HMS_CONCEPT_CODE) {
        handleStoredZipcode(
          storedZipcode,
          formConfig,
          setCurrentStep,
          setIsLoading,
          setFormValues,
        );
        return;
      }
      if (code === conceptCodes.MRA_CONCEPT_CODE) {
        handleMraStoredZipcode(
          storedZipcode,
          formConfig,
          setCurrentStep,
          setIsLoading,
          setOnline,
          setFormValues,
        );
        return;
      }
    }
  }

  if (
    isNeighborlyFlow &&
    currentStepConfig?.steptype === AppConstants.BOOK_AN_INSPECTION &&
    storedWebLocationId &&
    currentStep === 0 &&
    !storedZipcode
  ) {
    const code = formConfig?.conceptCode;
    if (code === conceptCodes.HMS_CONCEPT_CODE) {
      handleStoredWebLocationRedirect(
        currentStep,
        setFormValues,
        setCurrentStep,
        formConfig,
        storedWebLocationId,
        setIsLoading,
      );
      return;
    }
    if (code === conceptCodes.MRA_CONCEPT_CODE) {
      handleStoredWebLocationRedirectMRA(
        currentStep,
        setFormValues,
        setCurrentStep,
        formConfig,
        storedWebLocationId,
        setIsLoading,
        setOnline,
      );
      return;
    }
  }
};

function getCategoryDetails(
  serviceCategory: any[],
  categoryId: string | number,
) {
  const selectedCategory = serviceCategory?.find((cat) => cat.id == categoryId);
  return {
    name: selectedCategory?.name,
    cesname: selectedCategory?.cesname,
    categoryBasedQuestions: selectedCategory?.categoryBasedQuestions?.filter(
      (obj) => obj?.question !== "",
    ),
  };
}

function getVisibleServiceCategories(
  cesMenuServiceTypes: any[],
  serviceType: string,
) {
  if (!cesMenuServiceTypes?.length) {
    return [];
  }
  const selectedService = cesMenuServiceTypes.find((opt) => {
    const optCesName = opt.cesname?.toLowerCase();
    const serviceTypeLower = serviceType?.toLowerCase();
    const residentialTextLower = AppConstants.RESIDENTIAL_TEXT.toLowerCase();
    return (
      optCesName === serviceTypeLower ||
      (!serviceTypeLower && optCesName === residentialTextLower)
    );
  });
  if (!selectedService) {
    return [];
  }
  return selectedService;
}

function updateFormValuesWithCategoryDetails(
  categoryDetails: {
    name?: string;
    cesname?: string;
    categoryBasedQuestions?: any[];
  },
  setFormValues: (updater: (prev: any) => any) => void,
) {
  if (categoryDetails.name) {
    setFormValues((prev) => ({
      ...prev,
      serviceCategoryName: categoryDetails.name,
      serviceName: categoryDetails.cesname,
    }));
  } else {
    setFormValues((prev) => ({
      ...prev,
      [FORM_VALUES_KEYS.CATEGORY_BASED_QAS]: [],
    }));
  }
}

export {
  formatPhoneNumber,
  formatPhoneInputWithCursorPosition,
  validatePhoneNumber,
  validateEmail,
  validateZipCode,
  validateFirstNameAndLast,
  validateAddress,
  decodeHtmlEntities,
  getSessionAndCorrelationID,
  formatSelectedDate,
  formatFormValueDate,
  schedulerConfigJsonSequence,
  updateFormValuesWithCategoryBasedQAs,
  createGuid,
  getFileExtension,
  getFranchiseIdByLocationId,
  checkDomainCode,
  isCaPostalCode,
  isUsPostalCode,
  getEmailValidationError,
  getValidFormattedDate,
  formatMobileHeaderDate,
  validateSquareFeet,
  extractDomain,
  updateResidentialDetails,
  updateCommercialDetails,
  isWeekendDate,
  handleOfferDetails,
  handleISNRedirect,
  handleStoredWebLocationRedirect,
  handleHMSMultipleLocationPOS,
  handleStoredZipcode,
  handleMraStoredZipcode,
  handleStoredWebLocationRedirectMRA,
  skipToNextStep,
  getCategoryDetails,
  getVisibleServiceCategories,
  updateFormValuesWithCategoryDetails,
};
